import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { interval, Subscription } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableModule } from '@angular/material/table';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DataService } from '../../services/data.service';
import { AuthService } from '../../services/auth.service';
import { FileUploadService } from '../../services/file-upload.service';
import { LayoutService } from '../../services/layout.service';
import { AdMethodCatalogGet } from '../../models/anomaly-detection';
import { DataSourceGet } from '../../models/datasource';
import { AnomalyDetectionResultsComponent } from '../../components/anomaly-detection-results/anomaly-detection-results.component';
import { DatasourceInfoPanelComponent } from '../../components/datasource-info-panel/datasource-info-panel.component';
import { RenameDialogComponent, RenameDialogData } from '../../components/rename-dialog/rename-dialog.component';

interface MethodParameter {
  name: string;
  label: string;
  type: string;
  required: boolean;
  default?: any;
  description?: string;
  enum?: string[];
  minimum?: number;
  maximum?: number;
}

@Component({
  selector: 'app-anomaly-detection',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatTableModule,
    MatDialogModule,
    AnomalyDetectionResultsComponent,
    DatasourceInfoPanelComponent
  ],
  templateUrl: './anomaly-detection.component.html',
  styleUrl: './anomaly-detection.component.scss'
})
export class AnomalyDetectionComponent implements OnInit, OnDestroy {
  methods: AdMethodCatalogGet[] = [];
  filteredMethods: AdMethodCatalogGet[] = [];
  selectedMethod: AdMethodCatalogGet | null = null;
  loading = false;
  loadingMethods = false;
  private pollingSubscription: Subscription | null = null;
  loadingMessage: string = '';
  detectionCurrentStep: number | null = null;
  detectionTotalSteps: number | null = null;
  uploadProgress = 0;
  uploadedBytes = 0;
  totalBytes = 0;
  uploadStatusText = 'Uploading file...';
  fetchingDetectionResults = false;
  error: string | null = null;
  userPlanId: string | null = null;
  showUpgradeMessage = false;
  
  // Search and filter
  searchQuery = '';
  
  // Scroll detection
  hasScrollTop = false;
  hasScrollBottom = false;
  
  // Section toggle states
  isDataSectionExpanded = false;
  isMethodSectionExpanded = true;
  isRunSectionExpanded = true;
  isLeftSidebarCollapsed = false;
  
  // Editor state
  editorMode: 'data-source' | 'method-selection' | 'method-config' | 'upgrade' | 'results' | 'help' | null = null;
  editorTitle = '';
  editorSubtitle = '';
  isEditorVisible = false;
  editorData: any = null;
  parsedMethodDescription: any = null;
  runDate: Date | null = null;
  tempSelectedMethod: AdMethodCatalogGet | null = null; // Temporary selection before configuration
  showMethodTooltip = false;
  shouldAutoOpenMethodSelection = false; // Flag to auto-open method selection after methods load
  
  // Data from dashboard
  dataSourceId: string | null = null;
  dataSourceName: string | null = null;
  uploadedFileData: any = null;
  dataSourceDetails: any = null;
  selectedDataSource: DataSourceGet | null = null;
  runName: string | null = null;
  runId: string | null = null;
  
  // Parameter form
  parameterForm: FormGroup;
  requiredParameters: MethodParameter[] = [];
  advancedParameters: MethodParameter[] = [];
  
  // Detection results state
  hasDetectionResults = false;
  lastRunMethodKey: string | null = null;
  lastRunParams: Record<string, any> = {};
  
  // Available columns for multi-select (column and column-array parameters)
  availableColumns: Array<{name: string, type: string}> = [];
  allowedColumnTypes: string[] = []; // e.g., ['NUMERIC', 'DATE']
  
  // Get sorted columns with allowed columns first
  get sortedColumns(): Array<{name: string, type: string}> {
    if (!this.availableColumns || this.availableColumns.length === 0) {
      return [];
    }
    
    // If no type restrictions, return as-is
    if (!this.allowedColumnTypes || this.allowedColumnTypes.length === 0) {
      return this.availableColumns;
    }
    
    // Sort: allowed columns first, then disallowed
    return [...this.availableColumns].sort((a, b) => {
      const aAllowed = this.allowedColumnTypes.includes(a.type);
      const bAllowed = this.allowedColumnTypes.includes(b.type);
      
      if (aAllowed && !bAllowed) return -1;
      if (!aAllowed && bAllowed) return 1;
      return 0; // Keep original order within each group
    });
  }
  
  // Get only valid/allowed columns for display
  get validColumns(): Array<{name: string, type: string}> {
    if (!this.availableColumns || this.availableColumns.length === 0) {
      return [];
    }
    
    // If no type restrictions, all columns are valid
    if (!this.allowedColumnTypes || this.allowedColumnTypes.length === 0) {
      return this.availableColumns;
    }
    
    // Filter to only allowed columns
    return this.availableColumns.filter(col => this.allowedColumnTypes.includes(col.type));
  }
  
  // Get count of invalid/hidden columns
  get invalidColumnCount(): number {
    if (!this.availableColumns || this.availableColumns.length === 0) {
      return 0;
    }
    
    // If no type restrictions, no columns are invalid
    if (!this.allowedColumnTypes || this.allowedColumnTypes.length === 0) {
      return 0;
    }
    
    // Count columns that don't match allowed types
    return this.availableColumns.filter(col => !this.allowedColumnTypes.includes(col.type)).length;
  }

  constructor(
    private dataService: DataService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private fileUploadService: FileUploadService,
    public layoutService: LayoutService,
    private dialog: MatDialog
  ) {
    this.parameterForm = this.fb.group({});
  }

  onFinish(): void {
    this.router.navigate(['/dashboard']);
  }

  ngOnDestroy(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    // Get user plan ID
    const currentUser = this.authService.getCurrentUser();
    this.userPlanId = currentUser?.plan?.planId || null;

    // Check if we're viewing an existing run
    const runId = this.route.snapshot.paramMap.get('runId');
    if (runId) {
      this.runId = runId;
      this.loadExistingRun(runId);
      return; // Don't proceed with normal initialization
    }

    // Get data from navigation state
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras.state || history.state;
    
    if (state && state['dataSourceId']) {
      this.dataSourceId = state['dataSourceId'];
      this.dataSourceName = state['dataSourceName'];
    } else if (state && state['uploadedFileData']) {
      this.uploadedFileData = state['uploadedFileData'];
    }

    if (state && state['name']) {
      this.runName = state['name'];
    }

    // Launch method selection directly if data is available (BEFORE loading methods)
    if (!this.selectedMethod && (this.dataSourceId || this.uploadedFileData)) {
      // We'll open method selection after methods are loaded
      // Set a flag to trigger it
      this.shouldAutoOpenMethodSelection = true;
    }
    
    // Load available columns
    this.loadAvailableColumns();
    
    this.loadMethods();
  }
  
  async loadAvailableColumns(): Promise<void> {
    try {
      if (this.dataSourceId) {
        // Get columns from datasource
        const dataSource = await this.dataService.getDataSource(this.dataSourceId).toPromise();
        if (dataSource && dataSource.schemaJson) {
          const schema = JSON.parse(dataSource.schemaJson);
          // Handle different schema formats:
          // - New format: [{column: "ProductID", atomic_dtype: "numeric", soft_dtype: "numeric", stats: {...}}]
          // - Old array format: [{ColumnName: "id", DataType: "INT"}]
          // - Object with columns property: {columns: [...]}
          const columns = Array.isArray(schema) ? schema : (schema.columns || schema.Columns || []);
          this.availableColumns = columns.map((col: any) => ({
            name: col.column || col.name || col.Name || col.ColumnName,
            type: this.normalizeColumnType(col.atomic_dtype || col.type || col.Type || col.DataType)
          }));
        }
      } else if (this.uploadedFileData) {
        // Get columns from uploaded file data
        const dataArray = this.uploadedFileData.data || this.uploadedFileData;
        if (Array.isArray(dataArray) && dataArray.length > 0) {
          // Extract column names and infer types from first object
          const firstRow = dataArray[0];
          this.availableColumns = Object.keys(firstRow).map(columnName => ({
            name: columnName,
            type: this.inferColumnType(firstRow[columnName])
          }));
        }
      }
    } catch (error) {
      console.error('Error loading available columns:', error);
      this.availableColumns = [];
    }
  }
  
  // Normalize column type to standard format
  private normalizeColumnType(type: string): string {
    if (!type) return 'OTHER';
    const lowerType = type.toLowerCase();
    
    // Numeric types
    if (lowerType.includes('int') || lowerType.includes('number') || 
        lowerType.includes('float') || lowerType.includes('decimal') ||
        lowerType.includes('numeric') || lowerType.includes('double')) {
      return 'NUMERIC';
    }
    
    // Date/Time types
    if (lowerType.includes('date') || lowerType.includes('time') || lowerType.includes('timestamp')) {
      return 'DATE';
    }
    
    // String types
    if (lowerType === 'str' || lowerType.includes('string') || lowerType.includes('text') || 
        lowerType.includes('varchar') || lowerType.includes('char')) {
      return 'STRING';
    }
    
    // Boolean types
    if (lowerType.includes('bool')) {
      return 'BOOLEAN';
    }
    
    return 'OTHER';
  }
  
  // Infer column type from value
  private inferColumnType(value: any): string {
    if (value === null || value === undefined) {
      return 'OTHER';
    }
    
    const type = typeof value;
    
    if (type === 'number') {
      return 'NUMERIC';
    }
    
    if (type === 'boolean') {
      return 'BOOLEAN';
    }
    
    if (type === 'string') {
      // Try to detect date strings
      const datePatterns = [
        /^\d{4}-\d{2}-\d{2}/, // YYYY-MM-DD
        /^\d{2}\/\d{2}\/\d{4}/, // MM/DD/YYYY or DD/MM/YYYY
        /^\d{4}\/\d{2}\/\d{2}/ // YYYY/MM/DD
      ];
      
      if (datePatterns.some(pattern => pattern.test(value))) {
        return 'DATE';
      }
      
      return 'STRING';
    }
    
    return 'OTHER';
  }
  
  showHelpContent(): void {
    this.editorMode = 'help';
    this.editorTitle = 'Welcome to Anomaly Detection';
    this.editorSubtitle = 'Your workspace for identifying anomalies in your data';
    this.isEditorVisible = true;
    this.showMethodTooltip = true;
  }

  async loadMethods(): Promise<void> {
    this.loadingMethods = true;
    this.error = null;

    try {
      let methods = await this.dataService.getAvailableAdMethods().toPromise() || [];

      // Use the methods returned from the API directly
      if (!Array.isArray(methods)) {
        methods = [];
      }

      // Do not filter out unavailable methods here — availability gates usage
      // and is displayed in the UI as an upgrade requirement.

      // Sort by complexity (simpler methods first)
      methods.sort((a, b) => (a.complexity || 0) - (b.complexity || 0));
      this.methods = methods;

      // Initialize filtered methods
      this.filterMethods();
      
      // Auto-open method selection if flag is set
      if (this.shouldAutoOpenMethodSelection) {
        this.shouldAutoOpenMethodSelection = false; // Reset flag
        this.openMethodSelection();
      }
    } catch (error) {
      console.error('Error loading AD methods:', error);
      this.error = 'Failed to load anomaly detection methods';
      this.snackBar.open('Failed to load methods', '×', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
    } finally {
      this.loadingMethods = false;
    }
  }

  async loadExistingRun(runId: string): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      // First load the methods catalog so we have method definitions
      await this.loadMethods();

      // Then load the specific run
      const run = await this.dataService.getAnomalyDetectionRun(runId).toPromise();
      if (!run) {
        throw new Error('Run not found');
      }

      // Set the data source ID and run name from the run
      this.dataSourceId = run.dataSourceId || null;
      this.runName = run.name || null;
      
      // Load columns for this datasource if we have one
      if (this.dataSourceId) {
        await this.loadAvailableColumns();
      }

      // Find and select the method that was used
      const method = this.methods.find(m => m.methodKey === run.methodKey);
      if (!method) {
        throw new Error(`Method '${run.methodKey}' not found in catalog`);
      }

      // Parse the results JSON
      let parsedResults;
      try {
        parsedResults = JSON.parse(run.resultJson);
      } catch (e) {
        throw new Error('Failed to parse result JSON');
      }

      // Set the selected method
      this.selectedMethod = method;
      this.tempSelectedMethod = method;

      // Build the parameter form from the method definition
      this.buildParameterForm(method);

      // TODO: Extract and populate the parameters once service returns them properly
      // this.populateParametersFromResults(parsedResults);

      // Set the results - pass the full results object, not just report
      this.editorData = parsedResults;
      this.hasDetectionResults = true;
      this.lastRunMethodKey = run.methodKey;
      this.runDate = run.createdAt ? new Date(run.createdAt) : null;

      console.log('Setting editorData:', this.editorData);
      console.log('hasDetectionResults:', this.hasDetectionResults);

      // Show the results
      this.showResults();

      console.log('After showResults - editorMode:', this.editorMode);
      console.log('After showResults - isEditorVisible:', this.isEditorVisible);

    } catch (error: any) {
      console.error('Error loading existing run:', error);
      this.error = error.message || 'Failed to load anomaly detection run';
      this.snackBar.open(this.error || 'An error occurred', '×', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
    } finally {
      this.loading = false;
    }
  }

  private populateParametersFromResults(results: any): void {
    // Try to extract parameters from the results structure
    // The exact structure may vary, so we'll be defensive

    if (!this.selectedMethod) return;

    // Extract the columns that were analyzed from per_column keys
    if (results.report?.per_column) {
      const columnsUsed = Object.keys(results.report.per_column);
      if (this.parameterForm.contains('target_columns')) {
        // Set the columns in the form
        this.parameterForm.patchValue({
          target_columns: columnsUsed
        });
      }

      // Extract method parameters from the first column's method.params
      // (they should be the same for all columns)
      const firstColumn = Object.values(results.report.per_column)[0] as any;
      if (firstColumn?.method?.params) {
        const methodParams = firstColumn.method.params;
        Object.keys(methodParams).forEach(key => {
          if (this.parameterForm.contains(key)) {
            this.parameterForm.patchValue({
              [key]: methodParams[key]
            });
          }
        });
      }
    }
  }

  private showResults(): void {
    // Display the results in the editor
    this.editorMode = 'results';
    this.editorTitle = 'Detection Results';
    this.editorSubtitle = `Using ${this.selectedMethod?.name || 'method'}`;
    this.isEditorVisible = true;
  }

  // Filter methods based on search query
  filterMethods(): void {
    const query = this.searchQuery.toLowerCase().trim();
    
    if (!query) {
      this.filteredMethods = [...this.methods];
    } else {
      this.filteredMethods = this.methods.filter(method => {
        const nameMatch = method.name.toLowerCase().includes(query);
        const keyMatch = method.methodKey.toLowerCase().includes(query);
        const descMatch = method.description?.toLowerCase().includes(query) || false;
        const complexityMatch = this.getComplexityLabel(method.complexity).toLowerCase().includes(query);
        
        return nameMatch || keyMatch || descMatch || complexityMatch;
      });
    }
  }

  // Get the appropriate description for a method based on the current view mode
  getMethodDescription(method: AdMethodCatalogGet): string {
    const desc = this.layoutService.isAdvancedMode() ? (method.advancedDescription || method.description) : (method.simpleDescription || method.description);
    // If description is an object or a JSON string representing an object, prefer the `header` property for compact display
    const parsed = this.parseDescription(desc);
    if (parsed && typeof parsed === 'object') {
      return parsed.header || (Array.isArray(parsed.when) ? parsed.when.join('; ') : '') || method.description || '';
    }
    return parsed || '';
  }

  // Parse a description which may be a plain string, JSON string, or already an object.
  // Returns the original string when parsing fails, or the parsed object when successful.
  private parseDescription(value: any): any {
    if (!value && value !== '') return null;
    // If it's already an object, return as-is
    if (typeof value === 'object') return value;
    if (typeof value !== 'string') return String(value);

    // Try to parse JSON string
    try {
      const trimmed = value.trim();
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        return JSON.parse(trimmed);
      }
    } catch (e) {
      // ignore parse errors and fall through
    }

    // Fallback: return original string
    return value;
  }

  // Get a parsed description object for the currently selected view (simple/advanced)
  getParsedDescriptionForMethod(method: AdMethodCatalogGet): any {
    const desc = this.layoutService.isAdvancedMode() ? (method.advancedDescription || method.description) : (method.simpleDescription || method.description);
    return this.parseDescription(desc);
  }

  // Handle scroll for gradient visibility
  onMethodsContentScroll(event: Event): void {
    const element = event.target as HTMLElement;
    this.hasScrollTop = element.scrollTop > 20;
    this.hasScrollBottom = element.scrollHeight - element.scrollTop - element.clientHeight > 20;
  }

  // Open method selection in editor panel (Step 1)
  openMethodSelection(): void {
    this.tempSelectedMethod = this.selectedMethod; // Start with current selection if any
    this.editorMode = 'method-selection';
    this.editorTitle = 'Select Detection Method';
    this.editorSubtitle = 'Choose an anomaly detection algorithm';
    this.isEditorVisible = true;
    this.isMethodSectionExpanded = true;
  }

  // Select method and add to sidebar (Step 2)
  selectMethodForConfig(method: AdMethodCatalogGet): void {
    // Check if method is supported
    if (!this.isMethodSupported(method)) {
      this.tempSelectedMethod = method;
      this.editorMode = 'upgrade';
      this.editorTitle = 'Upgrade Required';
      this.editorSubtitle = `${method.name} requires a plan upgrade`;
      return;
    }

    // Supported method - add to sidebar and show advanced settings
    this.selectedMethod = method;
    this.tempSelectedMethod = method;
    this.parsedMethodDescription = this.getParsedDescriptionForMethod(method);
    
    // Show advanced settings screen
    this.editorMode = 'method-config';
    this.editorTitle = method.name;
    this.editorSubtitle = this.parsedMethodDescription && typeof this.parsedMethodDescription === 'object' ? (this.parsedMethodDescription.header || method.description || '') : (method.description || '');
    this.isEditorVisible = true;
    this.buildParameterForm(method);
    
    // Expand run section
    this.isRunSectionExpanded = true;
  }

  // Open method configuration editor
  editMethodConfig(method: AdMethodCatalogGet): void {
    this.tempSelectedMethod = method;
    this.editorMode = 'method-config';
    this.editorTitle = method.name;
    this.parsedMethodDescription = this.getParsedDescriptionForMethod(method);
    this.editorSubtitle = this.parsedMethodDescription && typeof this.parsedMethodDescription === 'object' ? (this.parsedMethodDescription.header || method.description || '') : (method.description || '');
    this.isEditorVisible = true;
    this.buildParameterForm(method);
  }

  // Back to method selection from configuration
  onBackToMethodSelection(): void {
    this.editorMode = 'method-selection';
    this.editorTitle = 'Select Detection Method';
    this.editorSubtitle = 'Choose an anomaly detection algorithm';
    this.parameterForm = this.fb.group({});
    this.requiredParameters = [];
    this.advancedParameters = [];
  }

  // Old method select (kept for compatibility but not used)
  onMethodSelect(method: AdMethodCatalogGet): void {
    this.selectedMethod = method;
    
    // Check if method is supported by user's plan
    if (!this.isMethodSupported(method)) {
      this.showUpgradeMessage = true;
      this.editorMode = 'upgrade';
      this.editorTitle = 'Upgrade Required';
      this.editorSubtitle = '';
      this.isEditorVisible = true;
      this.parameterForm = this.fb.group({});
      this.requiredParameters = [];
      this.advancedParameters = [];
    } else {
      this.showUpgradeMessage = false;
      this.editorMode = 'method-config';
      this.editorTitle = method.name;
      this.editorSubtitle = method.description || '';
      this.isEditorVisible = true;
      this.buildParameterForm(method);
    }
  }

  isMethodSupported(method: AdMethodCatalogGet): boolean {
    // If backend marked method as unavailable, require upgrade
    if (method.isAvailable === true) {
      return true;
    }
    return false;
  }

  // Section toggle methods
  toggleDataSection(): void {
    this.isDataSectionExpanded = !this.isDataSectionExpanded;
  }

  toggleMethodSection(): void {
    this.isMethodSectionExpanded = !this.isMethodSectionExpanded;
  }

  toggleRunSection(): void {
    this.isRunSectionExpanded = !this.isRunSectionExpanded;
  }

  toggleLeftSidebar(): void {
    this.isLeftSidebarCollapsed = !this.isLeftSidebarCollapsed;
  }

  // Editor methods
  async viewDataSource(): Promise<void> {
    if (this.dataSourceId) {
      this.editorMode = 'data-source';
      this.editorTitle = this.dataSourceName || 'Data Source';
      this.editorSubtitle = 'Click to Change Data';
      this.isEditorVisible = true;
      this.dataSourceDetails = null; // Clear previous details
      
      // Fetch data source details
      try {
        const dataSource = await this.dataService.getDataSource(this.dataSourceId).toPromise();
        this.selectedDataSource = dataSource || null;
        if (dataSource && dataSource.schemaJson) {
          const schema = JSON.parse(dataSource.schemaJson);
          // Handle new schema format: [{column, stats, ...}]
          if (Array.isArray(schema) && schema.length > 0 && schema[0].column) {
            this.dataSourceDetails = {
              rowCount: schema[0].stats?.n_rows || 0,
              columns: schema.map((col: any) => col.column),
              dataSourceName: dataSource.dataSourceName
            };
          }
          // Handle old format
          else {
            this.dataSourceDetails = {
              rowCount: schema.rowCount || schema.RowCount || 0,
              columns: schema.columns || schema.Columns || [],
              dataSourceName: dataSource.dataSourceName
            };
          }
        } else {
          this.dataSourceDetails = {
            rowCount: 0,
            columns: [],
            dataSourceName: dataSource?.dataSourceName || 'Unknown'
          };
        }
      } catch (error) {
        console.error('Error fetching data source details:', error);
        this.snackBar.open('Failed to load data source details', '×', {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.dataSourceDetails = {
          rowCount: 0,
          columns: [],
          dataSourceName: 'Error loading data'
        };
      }
    } else if (this.uploadedFileData) {
      // Handle uploaded file
      this.editorMode = 'data-source';
      this.editorTitle = this.uploadedFileData.name || 'Uploaded File';
      this.editorSubtitle = 'Click to Change Data';
      this.isEditorVisible = true;
      this.selectedDataSource = null; // No datasource for uploaded files
    }
  }

  navigateToDataSource(): void {
    if (this.dataSourceId) {
      this.router.navigate(['/datasource', this.dataSourceId]);
    }
  }

  getTypeClass(type: string): string {
    if (!type) return 'unknown';
    const lowerType = type.toLowerCase();
    if (lowerType.includes('int') || lowerType.includes('number') || lowerType.includes('float') || lowerType.includes('decimal')) {
      return 'numeric';
    } else if (lowerType.includes('string') || lowerType.includes('text') || lowerType.includes('varchar')) {
      return 'text';
    } else if (lowerType.includes('date') || lowerType.includes('time')) {
      return 'date';
    } else if (lowerType.includes('bool')) {
      return 'boolean';
    }
    return 'other';
  }

  closeEditor(): void {
    this.isEditorVisible = false;
    this.tempSelectedMethod = null;
    this.parsedMethodDescription = null;
    setTimeout(() => {
      this.editorMode = null;
      this.editorTitle = '';
      this.editorSubtitle = '';
      this.editorData = null;
    }, 200);
  }

  async onFileUpdate(file: File): Promise<void> {
    try {
      if (this.dataSourceId) {
        // Update existing datasource via API
        this.loading = true;
        this.uploadProgress = 0;
        this.uploadedBytes = 0;
        this.totalBytes = file.size;
        this.uploadStatusText = 'Uploading file...';
        
        // Send file directly as multipart/form-data (no base64 encoding needed)
        this.dataService.replaceFileDataSourceWithProgress(
          this.dataSourceId,
          file
        ).subscribe({
          next: (event: any) => {
            if (event.type === HttpEventType.UploadProgress) {
              // Track upload progress
              if (event.total) {
                this.totalBytes = event.total;
                this.uploadedBytes = event.loaded;
                this.uploadProgress = Math.round((100 * event.loaded) / event.total);
              }
            } else if (event.type === HttpEventType.Response) {
              // Upload complete, switch to indeterminate while processing
              this.uploadProgress = 100;
              this.uploadStatusText = 'Processing...';
              
              const result = event.body;
              if (result && (result.statusCode === 200 || result.statusCode === 204)) {
                
                
                // Refetch the datasource to get updated info
                this.dataService.getDataSource(this.dataSourceId!).subscribe({
                  next: (updatedDataSource) => {
                    this.selectedDataSource = updatedDataSource || null;
                    
                    // Update data source details
                    if (updatedDataSource?.schemaJson) {
                      const schema = JSON.parse(updatedDataSource.schemaJson);
                      // Handle new schema format: [{column, stats, ...}]
                      if (Array.isArray(schema) && schema.length > 0 && schema[0].column) {
                        this.dataSourceDetails = {
                          rowCount: schema[0].stats?.n_rows || 0,
                          columns: schema.map((col: any) => col.column),
                          dataSourceName: updatedDataSource.dataSourceName
                        };
                      }
                      // Handle old format
                      else {
                        this.dataSourceDetails = {
                          rowCount: schema.rowCount || schema.RowCount || 0,
                          columns: schema.columns || schema.Columns || [],
                          dataSourceName: updatedDataSource.dataSourceName
                        };
                      }
                    }
                    
                    // Reload available columns
                    this.loadAvailableColumns().then(() => {
                      this.loading = false;
                    });
                  },
                  error: (error) => {
                    console.error('Error refetching datasource:', error);
                    this.loading = false;
                  }
                });
              } else {
                throw new Error(result?.error || 'Failed to update data source');
              }
            }
          },
          error: (error) => {
            this.loading = false;
            throw error;
          }
        });
      } else if (this.uploadedFileData) {
        // Update local uploaded file
        const uploadedData = await this.fileUploadService.processFile(file);
        
        this.uploadedFileData = {
          name: uploadedData.fileName,
          data: uploadedData
        };
        
        // Update editor title
        this.editorTitle = uploadedData.fileName;
        
        // Reload available columns from new file
        await this.loadAvailableColumns();
        
        
      }
    } catch (error: any) {
      console.error('Error updating file:', error);
      this.snackBar.open(
        error?.message || 'Failed to update file',
        '×',
        {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        }
      );
    } finally {
      this.loading = false;
    }
  }

  canRunDetection(): boolean {
    // Check if method is selected and no upgrade message
    if (!this.selectedMethod || this.showUpgradeMessage) {
      return false;
    }
    
    // Check if all required parameters have values (form is valid)
    if (this.parameterForm && this.requiredParameters.length > 0) {
      return this.parameterForm.valid;
    }
    
    // If there are no required parameters, allow detection
    return true;
  }
  
  hasParametersChanged(): boolean {
    if (!this.hasDetectionResults || !this.selectedMethod) {
      return false;
    }
    
    // Check if method changed
    if (this.lastRunMethodKey !== this.selectedMethod.methodKey) {
      return true;
    }
    
    // Check if parameters changed
    const currentParams = this.parameterForm?.value || {};
    const currentParamsFiltered: Record<string, any> = {};
    Object.keys(currentParams).forEach(key => {
      const value = currentParams[key];
      if (value !== null && value !== '' && value !== undefined) {
        currentParamsFiltered[key] = value;
      }
    });
    
    return JSON.stringify(currentParamsFiltered) !== JSON.stringify(this.lastRunParams);
  }
  
  onViewResults(): void {
    if (!this.hasDetectionResults) {
      return;
    }
    
    // Switch back to results view
    this.editorMode = 'results';
    this.editorTitle = 'Detection Results';
    this.editorSubtitle = `Using ${this.selectedMethod?.name || 'method'}`;
    this.isEditorVisible = true;
  }

  onRunDetection(): void {
    if (!this.canRunDetection()) {
      return;
    }
    
    // Check if method has parameters that need configuration
    if (this.selectedMethod) {
      // Build parameter form if not already built
      if (!this.parameterForm || Object.keys(this.parameterForm.controls).length === 0) {
        this.buildParameterForm(this.selectedMethod);
      }
      
      // Check if there are any parameters without values (required or that lack defaults)
      const hasUnsetParams = this.requiredParameters.some(param => {
        const control = this.parameterForm?.get(param.name);
        const value = control?.value;
        return value === null || value === '' || value === undefined;
      });
      
      // If there are parameters without values, open advanced settings instead of running
      if (hasUnsetParams) {
        this.editorMode = 'method-config';
        this.editorTitle = this.selectedMethod.name || 'Configure Method';
        this.editorSubtitle = 'Please configure required parameters before running detection';
        this.isEditorVisible = true;
        
        // Show message to user that configuration is required
        this.snackBar.open('Please configure the required parameters', '×', {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          duration: 3000
        });
        return; // Don't run detection yet
      }
    }
    
    // Validate the form
    if (this.parameterForm && this.parameterForm.invalid) {
      this.snackBar.open('Please fill in all required fields', '×', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      return;
    }

    // Extract parameters from form
    const params: Record<string, any> = {};
    if (this.parameterForm) {
      Object.keys(this.parameterForm.value).forEach(key => {
        const value = this.parameterForm.value[key];
        // Only include non-null values
        if (value !== null && value !== '' && value !== undefined) {
          params[key] = value;
        }
      });
    }

    // Set loading state
    this.loading = true;
    this.loadingMessage = 'Starting Detection';
    this.detectionCurrentStep = null;
    this.detectionTotalSteps = null;
    this.uploadProgress = 0;
    this.uploadedBytes = 0;
    this.totalBytes = 0;
    this.uploadStatusText = 'Uploading file...';

    // Call appropriate endpoint based on data source type
    if (this.dataSourceId) {
      // Use datasource endpoint
      this.uploadStatusText = 'Ingesting file...';
      const request: any = {
        MethodId: this.selectedMethod!.methodKey,
        Params: params
      };

      this.dataService.performAnomalyDetectionOnDatasource(this.dataSourceId, request, undefined, this.runName || undefined)
        .subscribe({
          next: (event: any) => {
            if (event.type === HttpEventType.UploadProgress) {
              if (event.total) {
                this.totalBytes = event.total;
                this.uploadedBytes = event.loaded;
                this.uploadProgress = Math.round((100 * event.loaded) / event.total);
              }
            } else if (event.type === HttpEventType.Response) {
              this.uploadProgress = 100;
              this.uploadStatusText = 'Detecting anomalies...';
              const actionToken = event.body as string;
              this.loadingMessage = 'Detecting Anomalies...';
              this.detectionCurrentStep = null;
              this.detectionTotalSteps = null;
              if (this.pollingSubscription) {
                this.pollingSubscription.unsubscribe();
              }
              this.pollingSubscription = interval(3000).pipe(
                switchMap(() => this.dataService.getLongRunningActionStatus(actionToken)),
                takeWhile(status => status.status !== 2 && status.status !== 3 && status.status !== 4, true)
              ).subscribe({
                next: (status) => {
                  if (status.status === 2) {
                    setTimeout(() => {
                    this.fetchingDetectionResults = true;
                    this.dataService.getLongRunningActionResult(actionToken).subscribe({
                      next: (result) => {
                        const anomalyJson = result.anomalyResult?.resultJson;
                        if (!anomalyJson) {
                          this.loading = false;
                          this.fetchingDetectionResults = false;
                          this.handleDetectionError({ message: 'No anomaly result returned from server' });
                          return;
                        }
                        const response = JSON.parse(anomalyJson);
                        this.loading = false;
                        this.fetchingDetectionResults = false;
                        this.handleDetectionSuccess(response);
                      },
                      error: (error: any) => {
                        this.loading = false;
                        this.fetchingDetectionResults = false;
                        this.handleDetectionError(error);
                      }
                    });
                    }, 3000);
                  } else if (status.status === 3) {
                    this.loading = false;
                    this.handleDetectionError({ message: status.errorMessage || 'Anomaly detection failed' });
                  } else if (status.status === 4) {
                    this.loading = false;
                    this.handleDetectionError({ message: 'Anomaly detection completed but the results could not be saved. Please try again.' });
                  } else {
                    this.detectionCurrentStep = status.currentStep ?? null;
                    this.detectionTotalSteps = status.totalSteps ?? null;
                  }
                },
                error: (error: any) => {
                  this.loading = false;
                  this.handleDetectionError(error);
                }
              });
            }
          },
          error: (error: any) => {
            this.loading = false;
            this.handleDetectionError(error);
          }
        });
    } else if (this.uploadedFileData) {
      // Use uploaded file endpoint with progress tracking
      const uploadedData = this.fileUploadService.getUploadedData();
      if (!uploadedData?.originalFile) {
        this.loading = false;
        this.snackBar.open('Original file not available. Please re-upload the file.', '×', {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          duration: 5000,
          panelClass: ['error-snackbar']
        });
        return;
      }

      this.totalBytes = uploadedData.originalFile.size;

      this.dataService.performAnomalyDetectionWithProgress(
        uploadedData.originalFile,
        this.selectedMethod!.methodKey,
        params,
        this.runName || undefined
      ).subscribe({
          next: (event: any) => {
            if (event.type === HttpEventType.UploadProgress) {
              if (event.total) {
                this.totalBytes = event.total;
                this.uploadedBytes = event.loaded;
                this.uploadProgress = Math.round((100 * event.loaded) / event.total);
              }
            } else if (event.type === HttpEventType.Response) {
              this.uploadProgress = 100;
              this.uploadStatusText = 'Detecting anomalies...';
              const actionToken = event.body as string;
              this.loadingMessage = 'Detecting Anomalies...';
              this.detectionCurrentStep = null;
              this.detectionTotalSteps = null;
              if (this.pollingSubscription) {
                this.pollingSubscription.unsubscribe();
              }
              this.pollingSubscription = interval(3000).pipe(
                switchMap(() => this.dataService.getLongRunningActionStatus(actionToken)),
                takeWhile(status => status.status !== 2 && status.status !== 3 && status.status !== 4, true)
              ).subscribe({
                next: (status) => {
                  if (status.status === 2) {
                    setTimeout(() => {
                    this.fetchingDetectionResults = true;
                    this.dataService.getLongRunningActionResult(actionToken).subscribe({
                      next: (result) => {
                        const anomalyJson = result.anomalyResult?.resultJson;
                        if (!anomalyJson) {
                          this.loading = false;
                          this.fetchingDetectionResults = false;
                          this.handleDetectionError({ message: 'No anomaly result returned from server' });
                          return;
                        }
                        const response = JSON.parse(anomalyJson);
                        this.loading = false;
                        this.fetchingDetectionResults = false;
                        this.handleDetectionSuccess(response);
                      },
                      error: (error) => {
                        this.loading = false;
                        this.fetchingDetectionResults = false;
                        this.handleDetectionError(error);
                      }
                    });
                    }, 3000);
                  } else if (status.status === 3) {
                    this.loading = false;
                    this.handleDetectionError({ message: status.errorMessage || 'Anomaly detection failed' });
                  } else if (status.status === 4) {
                    this.loading = false;
                    this.handleDetectionError({ message: 'Anomaly detection completed but the results could not be saved. Please try again.' });
                  } else {
                    this.detectionCurrentStep = status.currentStep ?? null;
                    this.detectionTotalSteps = status.totalSteps ?? null;
                  }
                },
                error: (error) => {
                  this.loading = false;
                  this.handleDetectionError(error);
                }
              });
            }
          },
          error: (error) => {
            this.loading = false;
            this.handleDetectionError(error);
          }
        });
    } else {
      this.loading = false;
      this.snackBar.open('No data source available', '×', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
    }
  }

  private handleDetectionSuccess(response: any): void {
    // Store the response
    this.editorData = response;
    
    // Track detection state
    this.hasDetectionResults = true;
    this.lastRunMethodKey = this.selectedMethod?.methodKey || null;
    this.runDate = new Date();
    
    // Store parameters used for this run
    const params: Record<string, any> = {};
    Object.keys(this.parameterForm.value).forEach(key => {
      const value = this.parameterForm.value[key];
      if (value !== null && value !== '' && value !== undefined) {
        params[key] = value;
      }
    });
    this.lastRunParams = params;
    
    // Switch to results view
    this.editorMode = 'results';
    this.editorTitle = 'Detection Results';
    this.editorSubtitle = `Using ${this.selectedMethod?.name || 'method'}`;
    this.isEditorVisible = true;

    
  }

  private handleDetectionError(error: any): void {
    console.error('Error running anomaly detection:', error);
    
    const errorMessage = error.error?.message || error.message || 'Failed to run anomaly detection';
    
    this.snackBar.open(errorMessage, '×', {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['error-snackbar']
    });
  }

  private buildParameterForm(method: AdMethodCatalogGet): void {
    this.requiredParameters = [];
    this.advancedParameters = [];
    const formControls: { [key: string]: any } = {};
    
    // Parse inputsJson to get column type requirements
    this.allowedColumnTypes = [];
    if (method.inputsJson) {
      try {
        const inputsConfig = JSON.parse(method.inputsJson);
        const rawTargetCols = inputsConfig.params?.target_columns;
        if (rawTargetCols) {
          // Support both legacy pipe-delimited string ("NUMERIC|DATE|STR")
          // and the newer array form (["numeric", "date"]).
          let types: string[] = [];
          if (typeof rawTargetCols === 'string') {
            types = rawTargetCols.split('|').map((t: string) => t.trim());
          } else if (Array.isArray(rawTargetCols)) {
            types = rawTargetCols.map((t: any) => String(t).trim());
          } else {
            types = [String(rawTargetCols)];
          }

          // Normalize the allowed types to match our internal format
          this.allowedColumnTypes = types.map((t: string) => this.normalizeColumnType(t));
          console.log('Allowed column types for target_columns:', this.allowedColumnTypes);
        }
      } catch (error) {
        console.error('Error parsing inputsJson:', error);
      }
    }
    
    // Unsubscribe from previous form changes if any
    if (this.parameterForm) {
      // Check if parameters have actually changed before resetting
      const hasChanged = this.hasParametersChanged();
      if (hasChanged) {
        this.hasDetectionResults = false;
        this.editorData = null;
      }
    }

    if (method.paramsJson) {
      try {
        const paramsSchema = JSON.parse(method.paramsJson);
        
        // Parameters are directly in the schema, not under a "properties" key
        const properties = paramsSchema.properties || paramsSchema;
        const required = paramsSchema.required || [];

        Object.keys(properties).forEach(paramName => {
          const prop = properties[paramName];
          const isRequired = required.includes(paramName);
          const hasDefault = prop.default !== undefined && prop.default !== null;

          // Extract enum from either the property itself or from items.enum for array types
          let enumValues = prop.enum || (prop.anyOf?.[0]?.enum);
          if (prop.type === 'array' && prop.items?.enum) {
            enumValues = prop.items.enum;
          }

          const inputType = this.getInputType(prop);
          
          // Special handling: target_columns is always treated as column-array for backward compatibility
          const finalInputType = paramName === 'target_columns' ? 'column-array' : inputType;

          const parameter: MethodParameter = {
            name: paramName,
            label: prop.title || this.formatLabel(paramName),
            type: finalInputType,
            required: !hasDefault, // Parameter is required if it has no default value
            default: prop.default,
            description: prop.description,
            enum: enumValues,
            minimum: prop.minimum,
            maximum: prop.maximum
          };

          // Add to appropriate list
          // Parameters WITHOUT defaults go to required (shown at top)
          // Parameters WITH defaults go to advanced (shown below)
          if (!hasDefault) {
            this.requiredParameters.push(parameter);
            // Column arrays and regular arrays use array controls
            if (finalInputType === 'column-array' || paramName === 'target_columns') {
              formControls[paramName] = [[], Validators.required];
            } else {
              formControls[paramName] = ['', Validators.required];
            }
          } else {
            this.advancedParameters.push(parameter);
            // Column arrays and regular arrays use array controls with defaults
            if (finalInputType === 'column-array' || paramName === 'target_columns') {
              formControls[paramName] = [parameter.default || []];
            } else {
              formControls[paramName] = [parameter.default ?? ''];
            }
          }
        });

        this.parameterForm = this.fb.group(formControls);
      } catch (error) {
        console.error('Error parsing params schema:', error);
        this.snackBar.open('Error loading method parameters', '×', {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    }
  }

  private getInputType(prop: any): string {
    // Detect column type hints in various metadata conventions (matching select-goal-dialog)
    if (prop.ui_type === 'column' || 
        prop.input_type === 'column' || 
        prop.inputType === 'column' || 
        prop['x-input-type'] === 'column' || 
        prop.format === 'column' || 
        prop['x-format'] === 'column' || 
        prop.type === 'column') {
      return 'column';
    }
    
    // Check for array with column items
    if (prop.type === 'array' && prop.items) {
      if (prop.items.ui_type === 'column' || 
          prop.items.input_type === 'column' || 
          prop.items.inputType === 'column' || 
          prop.items['x-input-type'] === 'column' || 
          prop.items.format === 'column' || 
          prop.items['x-format'] === 'column') {
        return 'column-array';
      }
      // Check for array with enum (multiselect)
      if (prop.items.enum) {
        return 'multiselect';
      }
    }
    
    // Check for enum (single select)
    if (prop.enum || prop.anyOf?.[0]?.enum) {
      return 'select';
    }
    
    const type = prop.type || (prop.anyOf?.[0]?.type);
    
    switch (type) {
      case 'integer':
      case 'number':
        return 'number';
      case 'boolean':
        return 'checkbox';
      case 'string':
      default:
        return 'text';
    }
  }

  private formatLabel(name: string): string {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  onSubmit(): void {
    if (this.parameterForm.invalid) {
      this.snackBar.open('Please fill in all required fields', '×', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      return;
    }

    const params = this.parameterForm.value;
    
    // Remove empty advanced parameters
    Object.keys(params).forEach(key => {
      if (params[key] === '' || params[key] === null) {
        delete params[key];
      }
    });

    // Save selection
    if (this.tempSelectedMethod) {
      this.selectedMethod = this.tempSelectedMethod;
    }

    console.log('Submitting anomaly detection:', {
      method: this.selectedMethod,
      params,
      dataSourceId: this.dataSourceId,
      uploadedFileData: this.uploadedFileData
    });

    // Close editor and expand run section
    this.isMethodSectionExpanded = false;
    this.isRunSectionExpanded = true;
    this.closeEditor();

  }

  getComplexityLabel(complexity: number | null | undefined): string {
    if (!complexity) return 'Unknown';
    if (complexity <= 2) return 'Fast';
    if (complexity <= 4) return 'Intermediate';
    return 'Slow';
  }

  getComplexityClass(complexity: number | null | undefined): string {
    if (!complexity) return 'complexity-unknown';
    if (complexity <= 2) return 'complexity-fast';
    if (complexity <= 4) return 'complexity-intermediate';
    return 'complexity-slow';
  }

  get runTitleFontSize(): string {
    const name = this.runName || 'Anomaly Detection';
    const len = name.length;
    if (len <= 15) return '28px';
    if (len <= 22) return '22px';
    if (len <= 30) return '17px';
    if (len <= 40) return '15px';
    return '13px';
  }

  editRunName(): void {
    const currentName = this.runName || 'Anomaly Detection';
    
    const dialogRef = this.dialog.open(RenameDialogComponent, {
      width: '520px',
      data: {
        currentName: currentName,
        itemType: 'anomaly detection run'
      } as RenameDialogData
    });

    dialogRef.afterClosed().subscribe((newName: string | undefined) => {
      if (newName && newName !== currentName) {
        // If this is an existing run (has runId), update via API
        if (this.runId) {
          this.dataService.renameAnomalyDetectionRun(this.runId, newName).subscribe({
            next: (success: boolean) => {
              if (success) {
                this.runName = newName;
              }
            },
            error: (error) => {
              console.error('Error renaming run:', error);
              this.snackBar.open(error.message || 'Failed to rename run', '×', {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
              });
            }
          });
        } else {
          // For new runs, just update the local name that will be used when starting
          this.runName = newName;
        }
      }
    });
  }

  // Helper methods for results display
  getAnomalyColumns(): string[] {
    if (!this.editorData?.anomalies || this.editorData.anomalies.length === 0) {
      return [];
    }
    // Get all unique keys from the first anomaly record
    return Object.keys(this.editorData.anomalies[0]);
  }

  formatColumnName(column: string): string {
    // Convert snake_case or camelCase to Title Case
    return column
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  }

  // New helper methods for per-column results
  private expandedColumns = new Set<string>();

  getColumnNames(): string[] {
    if (!this.editorData?.report?.per_column) {
      return [];
    }
    return Object.keys(this.editorData.report.per_column);
  }

  getColumnResult(columnName: string): any {
    return this.editorData?.report?.per_column?.[columnName];
  }

  toggleColumnExpanded(columnName: string): void {
    if (this.expandedColumns.has(columnName)) {
      this.expandedColumns.delete(columnName);
    } else {
      this.expandedColumns.add(columnName);
    }
  }

  isColumnExpanded(columnName: string): boolean {
    return this.expandedColumns.has(columnName);
  }
  
  // Helper methods for column-array multi-select (generic version supporting any column parameter)
  isColumnSelected(columnName: string, paramName: string = 'target_columns'): boolean {
    const columnControl = this.parameterForm?.get(paramName);
    if (!columnControl) return false;
    const selectedColumns = columnControl.value || [];
    return selectedColumns.includes(columnName);
  }
  
  isColumnAllowed(columnName: string): boolean {
    // If no type restrictions, all columns are allowed
    if (!this.allowedColumnTypes || this.allowedColumnTypes.length === 0) {
      return true;
    }
    
    // Find the column and check if its type matches any allowed type
    const column = this.availableColumns.find(col => col.name === columnName);
    if (!column) return false;
    
    return this.allowedColumnTypes.includes(column.type);
  }
  
  toggleColumnSelection(columnName: string, paramName: string = 'target_columns'): void {
    // Don't allow toggling of disallowed columns
    if (!this.isColumnAllowed(columnName)) {
      return;
    }
    
    const columnControl = this.parameterForm?.get(paramName);
    if (!columnControl) return;
    
    const selectedColumns = columnControl.value || [];
    const index = selectedColumns.indexOf(columnName);
    
    if (index > -1) {
      // Remove column
      selectedColumns.splice(index, 1);
    } else {
      // Add column
      selectedColumns.push(columnName);
    }
    
    columnControl.setValue([...selectedColumns]);
    columnControl.markAsTouched();
  }
  
  selectAllColumns(paramName: string = 'target_columns'): void {
    const columnControl = this.parameterForm?.get(paramName);
    if (!columnControl) return;
    
    // Only select allowed columns
    const allowedColumnNames = this.availableColumns
      .filter(col => this.isColumnAllowed(col.name))
      .map(col => col.name);
    
    columnControl.setValue(allowedColumnNames);
    columnControl.markAsTouched();
  }
  
  deselectAllColumns(paramName: string = 'target_columns'): void {
    const columnControl = this.parameterForm?.get(paramName);
    if (!columnControl) return;
    
    columnControl.setValue([]);
    columnControl.markAsTouched();
  }

  getFileSizeString(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  /**
   * Map raw data type to friendly display name
   */
  getFriendlyDataTypeName(dataType: string): string {
    if (!dataType) return '';
    
    const lowerType = dataType.toLowerCase();
    
    // Map common data types to friendly names
    const typeMap: { [key: string]: string } = {
      'numeric': 'Number',
      'str': 'Text',
      'string': 'Text',
      'bool': 'Bool',
      'boolean': 'Bool',
      'bit': 'Bool',
      'date': 'Date',
      'datetime': 'Date',
      'time': 'Time'
    };
    
    return typeMap[lowerType] || dataType;
  }
}
